import React from 'react';
import { ReportData } from '../../types/report.types';
import HtmlEditor from '../HtmlEditor';

interface ScopeEditorV2Props {
  reportData: ReportData;
  onHtmlChange: (field: keyof ReportData, value: string) => void;
  onTargetDetailsChange: (index: number, field: string, value: any) => void;
  onAddTarget: () => void;
  onRemoveTarget: (index: number) => void;
  onDataChange: (field: string, value: any) => void;
}

function isV2Target(target: any): boolean {
  return !!(target.target_value !== undefined || target.target_type !== undefined);
}

const ScopeEditorV2: React.FC<ScopeEditorV2Props> = ({
  reportData,
  onHtmlChange,
  onTargetDetailsChange,
  onAddTarget,
  onRemoveTarget,
  onDataChange
}) => {
  const isNetworkTarget = (t: any) => {
    const type = (t.target_type || t.type || '').toLowerCase();
    const value = (t.target_value || t.url || t.target_name || '').toLowerCase();
    if (type.includes('web') || type.includes('api') || type.includes('app') || type.includes('mobile') || type.includes('ios') || type.includes('android')) {
      return false;
    }
    if (value.startsWith('http://') || value.startsWith('https://')) {
      return false;
    }
    return type.includes('network') || type.includes('infra') || type.includes('subnet') || type.includes('range') || type.includes('ip') ||
           !!value.match(/^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}/) ||
           /\/\s*\d{1,2}\s*$/.test(value);
  };

  const targets = reportData.target_details || [];
  const hasNetworkTargets = targets.some(isNetworkTarget);
  const isNetwork = reportData.testing_type?.toLowerCase().includes('network') ||
    reportData.program_details?.[0]?.testing_type?.toLowerCase().includes('network') || hasNetworkTargets;

  const config = reportData.finding_config || { globalFields: { description: true, instructions: true, impact: true, fix: true, evidence: true }, individualFindings: {} };

  const uniqueHostsWithFindings = new Set(
    (reportData.detailed_findings || [])
      .map((f: any) => (f.scope || '').trim())
      .filter(Boolean)
  ).size;

  const dynamicTotalAddresses = targets.filter(isNetworkTarget).length || targets.length || 0;
  const dynamicLiveHosts = targets.filter(isNetworkTarget).length || targets.length || 0;
  const dynamicHostsWithFindings = uniqueHostsWithFindings;
  const dynamicPosition = "External & Internal Network Security Assessment";

  const networkSummary = config.networkScopeSummary || {
    totalAddresses: String(dynamicTotalAddresses),
    liveHosts: String(dynamicLiveHosts),
    hostsWithFindings: String(dynamicHostsWithFindings),
    position: dynamicPosition
  };

  const handleSummaryChange = (field: string, value: string) => {
    const updated = {
      ...networkSummary,
      [field]: value
    };
    onDataChange('finding_config', { ...config, networkScopeSummary: updated });
  };

  return (
    <div className="h-full overflow-y-auto space-y-6">
      {/* Network Scope Counts Configuration */}
      {isNetwork && (
        <div className="bg-white rounded-xl shadow-lg border border-slate-100 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-1 h-8 bg-gradient-to-b from-blue-500 to-blue-700 rounded-full" />
            <div>
              <h3 className="text-base font-bold text-blue-900 tracking-tight">Network Scope Statistics</h3>
              <p className="text-xs text-blue-700/80 font-medium mt-0.5">Customize live host discovery metrics for the summary table</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 pt-2">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Total Addresses in Scope</label>
              <input
                type="text"
                value={networkSummary.totalAddresses || ''}
                onChange={(e) => handleSummaryChange('totalAddresses', e.target.value)}
                className="w-full px-2.5 py-1.5 text-xs border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
                placeholder="e.g., ~1,300"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Live Hosts Discovered</label>
              <input
                type="text"
                value={networkSummary.liveHosts || ''}
                onChange={(e) => handleSummaryChange('liveHosts', e.target.value)}
                className="w-full px-2.5 py-1.5 text-xs border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
                placeholder="e.g., 312"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Hosts with Findings</label>
              <input
                type="text"
                value={networkSummary.hostsWithFindings || ''}
                onChange={(e) => handleSummaryChange('hostsWithFindings', e.target.value)}
                className="w-full px-2.5 py-1.5 text-xs border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
                placeholder="e.g., 47"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Testing Position</label>
              <input
                type="text"
                value={networkSummary.position || ''}
                onChange={(e) => handleSummaryChange('position', e.target.value)}
                className="w-full px-2.5 py-1.5 text-xs border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
                placeholder="e.g., Assumed breach - standard domain user + LAN access"
              />
            </div>
          </div>
        </div>
      )}

      {/* Target Details Section */}
      <div className="bg-white rounded-xl shadow-lg border border-slate-100 p-6">
        <div className="flex items-center gap-3 mb-4 justify-between">
          <div className="flex items-center gap-3">
            <div className="w-1 h-8 bg-gradient-to-b from-blue-500 to-blue-700 rounded-full" />
            <div>
              <h3 className="text-base font-bold text-blue-900 tracking-tight">
                {isNetwork ? 'In-Scope Ranges / Subnets & Target Assets' : 'In-Scope Target Assets'}
              </h3>
              <p className="text-xs text-blue-700/80 font-medium mt-0.5">
                {isNetwork ? 'Manage subnet scopes, counts, descriptions and web targets' : 'Manage assessment targets, descriptions, and tech stacks'}
              </p>
            </div>
          </div>
          <button
            onClick={onAddTarget}
            className="px-3 py-1.5 bg-blue-600 text-white text-xs rounded-md hover:bg-blue-700 transition-colors duration-200 flex items-center gap-1.5 shadow-sm"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
            </svg>
            Add Range / Target
          </button>
        </div>
        <div className="pt-2">
          {reportData.target_details?.length === 0 ? (
            <div className="text-center py-8 bg-blue-50 rounded-lg border border-dashed border-blue-200">
              <p className="text-sm text-blue-700">No targets added yet</p>
              <button onClick={onAddTarget} className="mt-2 text-blue-600 hover:text-blue-700 text-sm font-medium">
                Add your first target
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {reportData.target_details?.map((target, index) => {
                const targetIsNetwork = isNetworkTarget(target);
                return (
                  <div key={index} className="group relative bg-blue-50 rounded-lg p-4 border border-blue-100 hover:border-blue-300 transition-colors duration-200">
                    <div className="flex items-start gap-3">
                      <div className="flex-1 space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-semibold text-blue-900 mb-1">
                              {targetIsNetwork ? 'Subnet / Range / Type' : 'Target Type'}
                            </label>
                            <input
                              type="text"
                              value={target.target_type || target.type || ''}
                              onChange={(e) => onTargetDetailsChange(index, isV2Target(target) ? 'target_type' : 'type', e.target.value)}
                              className="w-full px-2.5 py-1.5 text-sm border border-blue-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
                              placeholder={targetIsNetwork ? 'e.g., Network/Infrastructure' : 'e.g., Web Application'}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-blue-900 mb-1">
                              {targetIsNetwork ? 'Addressable Range / IP' : 'Target URL / Asset Name'}
                            </label>
                            <input
                              type="text"
                              value={target.target_value || target.url || ''}
                              onChange={(e) => onTargetDetailsChange(index, isV2Target(target) ? 'target_value' : 'url', e.target.value)}
                              className="w-full px-2.5 py-1.5 text-sm border border-blue-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
                              placeholder={targetIsNetwork ? 'e.g., 10.10.0.0/16 or 10.20.0.10' : 'e.g., https://app.example.com'}
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-semibold text-blue-900 mb-1">
                              {targetIsNetwork ? 'Estimated Hosts Count / Description' : 'Scope Description'}
                            </label>
                            <input
                              type="text"
                              value={target.description || ''}
                              onChange={(e) => onTargetDetailsChange(index, 'description', e.target.value)}
                              className="w-full px-2.5 py-1.5 text-sm border border-blue-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
                              placeholder={targetIsNetwork ? 'e.g., ~1,000 hosts' : 'e.g., Customer Portal'}
                            />
                          </div>
                          {!targetIsNetwork && (
                            <div>
                              <label className="block text-xs font-semibold text-blue-900 mb-1">Tech Stack</label>
                              <input
                                type="text"
                                value={target.tech_stack || target.technology || ''}
                                onChange={(e) => onTargetDetailsChange(index, 'tech_stack', e.target.value)}
                                className="w-full px-2.5 py-1.5 text-sm border border-blue-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
                                placeholder="e.g., React, Node.js"
                              />
                            </div>
                          )}
                        </div>

                        {/* User Role Configurations - Only for Web/API targets */}
                        {!targetIsNetwork && (
                          <div className="border-t border-blue-200/50 pt-3 mt-2">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-[11px] font-bold text-blue-800 uppercase tracking-wider block">User Role Settings</span>
                              <button
                                type="button"
                                onClick={() => {
                                  const currentRoles = target.roles && Array.isArray(target.roles) ? target.roles : (
                                    (target.role || target.privilege_level || target.credentials) ? [
                                      { role: target.role || '', privilege_level: target.privilege_level || '', credentials: target.credentials || '' }
                                    ] : []
                                  );
                                  onTargetDetailsChange(index, 'roles', [...currentRoles, { role: '', privilege_level: '', credentials: '' }]);
                                }}
                                className="px-2 py-0.5 bg-blue-100 text-blue-700 text-[10px] font-bold rounded hover:bg-blue-200 transition-colors"
                              >
                                + Add Role
                              </button>
                            </div>
                            {(() => {
                              const currentRoles = target.roles && Array.isArray(target.roles) ? target.roles : (
                                (target.role || target.privilege_level || target.credentials) ? [
                                  { role: target.role || '', privilege_level: target.privilege_level || '', credentials: target.credentials || '' }
                                ] : []
                              );
                              return currentRoles.length === 0 ? (
                                <p className="text-[10px] text-gray-500 italic">No roles added. Click "+ Add Role" to specify credentials.</p>
                              ) : (
                                <div className="space-y-2">
                                  {currentRoles.map((r: any, rIdx: number) => (
                                    <div key={rIdx} className="flex gap-2 items-end bg-white p-2 rounded-md border border-blue-100/70">
                                      <div className="flex-1">
                                        <label className="block text-[9px] font-medium text-gray-500 mb-0.5">Role Name</label>
                                        <input
                                          type="text"
                                          value={r.role || ''}
                                          onChange={(e) => {
                                            const updatedRoles = [...currentRoles];
                                            updatedRoles[rIdx] = { ...updatedRoles[rIdx], role: e.target.value };
                                            onTargetDetailsChange(index, 'roles', updatedRoles);
                                          }}
                                          className="w-full px-2.5 py-1 text-xs border border-blue-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
                                          placeholder="e.g., Standard User"
                                        />
                                      </div>
                                      <div className="flex-1">
                                        <label className="block text-[9px] font-medium text-gray-500 mb-0.5">Privilege Level</label>
                                        <input
                                          type="text"
                                          value={r.privilege_level || ''}
                                          onChange={(e) => {
                                            const updatedRoles = [...currentRoles];
                                            updatedRoles[rIdx] = { ...updatedRoles[rIdx], privilege_level: e.target.value };
                                            onTargetDetailsChange(index, 'roles', updatedRoles);
                                          }}
                                          className="w-full px-2.5 py-1 text-xs border border-blue-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
                                          placeholder="e.g., Low / High"
                                        />
                                      </div>
                                      <div className="flex-1">
                                        <label className="block text-[9px] font-medium text-gray-500 mb-0.5">Credentials</label>
                                        <input
                                          type="text"
                                          value={r.credentials || ''}
                                          onChange={(e) => {
                                            const updatedRoles = [...currentRoles];
                                            updatedRoles[rIdx] = { ...updatedRoles[rIdx], credentials: e.target.value };
                                            onTargetDetailsChange(index, 'roles', updatedRoles);
                                          }}
                                          className="w-full px-2.5 py-1 text-xs border border-blue-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
                                          placeholder="e.g., Provided"
                                        />
                                      </div>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const updatedRoles = currentRoles.filter((_: any, idx: number) => idx !== rIdx);
                                          onTargetDetailsChange(index, 'roles', updatedRoles);
                                        }}
                                        className="text-red-500 hover:text-red-700 p-1 flex-shrink-0 transition-colors"
                                        title="Remove role"
                                      >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              );
                            })()}
                          </div>
                        )}
                      </div>
                      <button
                        onClick={() => onRemoveTarget(index)}
                        className="opacity-0 group-hover:opacity-100 text-blue-400 hover:text-red-600 transition-all duration-200 flex-shrink-0 mt-2"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Out of Scope / Exclusions Notes */}
      <div className="bg-white rounded-xl shadow-lg border border-slate-100 p-6 mb-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-1 h-8 bg-gradient-to-b from-blue-500 to-blue-700 rounded-full" />
          <div>
            <h3 className="text-base font-bold text-blue-900 tracking-tight">Out of Scope / Exclusions</h3>
            <p className="text-xs text-blue-700/80 font-medium mt-0.5">List restrictions, exclusions, or out of scope items (one per line)</p>
          </div>
        </div>
        <div className="pt-2">
          <HtmlEditor
            field="scope_notes"
            title=""
            reportData={reportData}
            onHtmlChange={onHtmlChange}
            useJodit={true}
          />
        </div>
      </div>
    </div>
  );
};

export default ScopeEditorV2;
